import { type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Smoothly collapses/expands its children's height via a CSS `grid-template-rows`
 * transition (0fr → 1fr). Because it animates a single continuous property, a fast
 * toggle reverses cleanly from wherever it is — unlike DOM insert/remove animations,
 * which fight themselves and glitch on rapid toggling.
 *
 * The children stay mounted at all times; when collapsed they're zero-height,
 * `overflow: hidden`, and `inert` (out of tab order and the accessibility tree).
 * Keeping them mounted means any form state they hold survives a collapse.
 *
 * Put the leading/trailing gap *inside* `children` (e.g. `pt-5`) so it collapses
 * along with the content and leaves no empty slot behind in a gapped flex/grid stack.
 * Override the default 300ms via `className` (e.g. `duration-500`) — tailwind-merge
 * resolves the conflict in favour of the caller's value.
 */
export function Collapse({ open, children, className }: { open: boolean; children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        className,
      )}
    >
      <div className="overflow-hidden" inert={!open}>
        {children}
      </div>
    </div>
  )
}
